import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { MIDIDevice, MIDINoteEvent, ActiveNotes } from '@/types';

interface MIDIStore {
  // State
  isSupported: boolean;
  isConnected: boolean;
  devices: MIDIDevice[];
  selectedDeviceId: string | null;
  activeNotes: ActiveNotes;

  // Note event history (for analysis)
  noteHistory: MIDINoteEvent[];
  maxHistorySize: number;

  // Actions
  setSupported: (supported: boolean) => void;
  setConnected: (connected: boolean) => void;
  setDevices: (devices: MIDIDevice[]) => void;
  selectDevice: (deviceId: string | null) => void;

  // Note events
  noteOn: (pitch: number, velocity: number) => void;
  noteOff: (pitch: number) => void;
  clearActiveNotes: () => void;

  // History
  addToHistory: (event: MIDINoteEvent) => void;
  clearHistory: () => void;
}

export const useMIDIStore = create<MIDIStore>()(
  immer((set) => ({
    isSupported: typeof navigator !== 'undefined' && 'requestMIDIAccess' in navigator,
    isConnected: false,
    devices: [],
    selectedDeviceId: null,
    activeNotes: {
      notes: new Set(),
      lastEventTime: 0,
    },
    noteHistory: [],
    maxHistorySize: 1000,

    setSupported: (supported) =>
      set((state) => {
        state.isSupported = supported;
      }),

    setConnected: (connected) =>
      set((state) => {
        state.isConnected = connected;
      }),

    setDevices: (devices) =>
      set((state) => {
        state.devices = devices;
      }),

    selectDevice: (deviceId) =>
      set((state) => {
        state.selectedDeviceId = deviceId;
      }),

    noteOn: (pitch, velocity) =>
      set((state) => {
        state.activeNotes.notes.add(pitch);
        state.activeNotes.lastEventTime = performance.now();

        // Add to history
        const event: MIDINoteEvent = {
          type: 'noteon',
          pitch,
          velocity,
          timestamp: performance.now(),
        };
        state.noteHistory.push(event);

        // Trim history if needed
        if (state.noteHistory.length > state.maxHistorySize) {
          state.noteHistory = state.noteHistory.slice(-state.maxHistorySize);
        }
      }),

    noteOff: (pitch) =>
      set((state) => {
        state.activeNotes.notes.delete(pitch);
        state.activeNotes.lastEventTime = performance.now();

        // Add to history
        const event: MIDINoteEvent = {
          type: 'noteoff',
          pitch,
          velocity: 0,
          timestamp: performance.now(),
        };
        state.noteHistory.push(event);

        // Trim history if needed
        if (state.noteHistory.length > state.maxHistorySize) {
          state.noteHistory = state.noteHistory.slice(-state.maxHistorySize);
        }
      }),

    clearActiveNotes: () =>
      set((state) => {
        state.activeNotes.notes = new Set();
      }),

    addToHistory: (event) =>
      set((state) => {
        state.noteHistory.push(event);
        if (state.noteHistory.length > state.maxHistorySize) {
          state.noteHistory = state.noteHistory.slice(-state.maxHistorySize);
        }
      }),

    clearHistory: () =>
      set((state) => {
        state.noteHistory = [];
      }),
  }))
);
